import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../lib/api.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import type { Company } from '@mayksel/types';
import type { PaginatedResponse } from '@mayksel/types';
import type { CreateCompanyInput, UpdateCompanyInput } from '@mayksel/validators';

function useApiOpts() {
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  return { getToken, workspaceId };
}

export interface CompanyFilters {
  search?: string;
  type?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export function useCompanies(filters: CompanyFilters = {}) {
  const { getToken, workspaceId } = useApiOpts();
  const { search, type, page = 1, limit = 50, sortBy = 'created_at', sortDir = 'desc' } = filters;

  return useQuery<PaginatedResponse<Company>>({
    queryKey: ['companies', workspaceId, search, type, page, limit, sortBy, sortDir],
    queryFn: async () => {
      const token = await getToken();
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (type) params.set('type', type);
      params.set('page', String(page));
      params.set('limit', String(limit));
      params.set('sortBy', sortBy);
      params.set('sortDir', sortDir);

      const opts: { token?: string | undefined; workspaceId?: string | undefined } = {};
      if (token) opts.token = token;
      if (workspaceId) opts.workspaceId = workspaceId;
      return api.get<PaginatedResponse<Company>>(
        `/v1/companies?${params.toString()}`,
        opts,
      );
    },
    enabled: !!workspaceId,
  });
}

export function useCompany(id: string) {
  const { getToken, workspaceId } = useApiOpts();

  return useQuery<Company>({
    queryKey: ['companies', workspaceId, id],
    queryFn: async () => {
      const token = await getToken();
      return api.get<Company>(`/v1/companies/${id}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId && !!id,
  });
}

export function useCreateCompany() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCompanyInput) => {
      const token = await getToken();
      return api.post<Company>('/v1/companies', data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies', workspaceId] });
    },
  });
}

export function useUpdateCompany(id: string) {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateCompanyInput) => {
      const token = await getToken();
      return api.patch<Company>(`/v1/companies/${id}`, data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies', workspaceId] });
    },
  });
}

export function useDeleteCompany() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return api.delete<{ id: string }>(`/v1/companies/${id}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies', workspaceId] });
    },
  });
}
